/**
 * Shared file-ingest primitives.
 *
 * Every upload path in this app faces the same three problems before it can look at
 * a single value:
 *
 *   1. what KIND of file is this, really?
 *   2. turn it into a rectangular grid
 *   3. where in that grid does the data actually start?
 *
 * They were being solved separately, badly, and only for one path each:
 *
 *   · `apSpendFileParser.ts:235` accepts a file on MIME **or** extension, while
 *     `apSpendFiles.ts:261` accepts on MIME **only** — so a file can parse and then
 *     be rejected at storage, 200 lines apart.
 *   · `apSpendFileParser.ts:153` takes `worksheet.getRow(1)` as the header,
 *     unconditionally. Any file with a title row, an export banner or a leading
 *     blank silently maps garbage: the "headers" become the banner text and every
 *     real column lands unmapped. GuruFocus exports have THREE preamble rows, so
 *     files of that shape demonstrably exist in this domain.
 *   · nothing anywhere sniffs content, so the 43 GuruFocus `.xls` files that are
 *     actually tab-separated text (8% of that corpus) would be handed to ExcelJS
 *     and rejected.
 *
 * Solving them once here means an improvement to detection, delimiter handling or
 * header discovery reaches AP spend files and GuruFocus financials together,
 * instead of being fixed in whichever path someone happened to be looking at.
 *
 * Deliberately NOT here: what a column or row MEANS. Structure discovery is
 * deterministic and auditable; semantic mapping is fuzzy and belongs with
 * `smartMappingService`, which already does it with confidence scores and learning.
 * Keeping them apart means a bad guess about meaning can never corrupt the reading
 * of structure.
 */

export type FileFormat = "xlsx" | "delimited";

/** A sheet reduced to strings, so every downstream path shares one shape. */
export type Grid = string[][];

/**
 * Identify a file by its BYTES, never its name.
 *
 * `.xlsx` is a zip and starts `PK`. Everything else is treated as delimited text,
 * which is what the 43 mislabelled GuruFocus `.xls` files actually are.
 */
export function detectFileFormat(bytes: Uint8Array): FileFormat {
  const isZip =
    bytes.length > 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    (bytes[2] === 0x03 || bytes[2] === 0x05 || bytes[2] === 0x07);
  return isZip ? "xlsx" : "delimited";
}

/**
 * Pick the delimiter by counting candidates on the most populated lines.
 *
 * Guessing from the extension fails on the GuruFocus `.xls` files (tab-separated)
 * and on any customer CSV exported with semicolons — a European Excel default that
 * would otherwise parse as one giant column.
 */
export function detectDelimiter(text: string): string {
  const lines = text
    .split(/\r?\n/)
    .filter((l) => l.trim())
    .slice(0, 40);
  if (lines.length === 0) return ",";

  const candidates = ["\t", ",", ";", "|"];
  let best = ",";
  let bestScore = -1;

  for (const d of candidates) {
    const counts = lines.map((l) => l.split(d).length - 1).filter((n) => n > 0);
    if (counts.length === 0) continue;
    // Consistency matters more than raw frequency: a real delimiter produces a
    // similar count on most lines, whereas a comma inside prose does not.
    const mean = counts.reduce((a, b) => a + b, 0) / counts.length;
    const variance = counts.reduce((a, b) => a + (b - mean) ** 2, 0) / counts.length;
    const score = mean * (counts.length / lines.length) - Math.sqrt(variance);
    if (score > bestScore) {
      bestScore = score;
      best = d;
    }
  }
  return best;
}

/** Split delimited text into a grid, stripping the quotes exporters wrap cells in. */
export function delimitedToGrid(text: string, delimiter = detectDelimiter(text)): Grid {
  return text
    .split(/\r?\n/)
    .map((line) => line.split(delimiter).map((v) => v.replace(/^"|"$/g, "").trim()));
}

/**
 * `"1,234.5"` -> 1234.5 · `"-"` -> null · `"(500)"` -> -500.
 *
 * Returns null, never 0, for an unavailable figure. GuruFocus prints a bare hyphen
 * and accounting exports print `(500)` for negatives; a fabricated 0 in a financial
 * record is worse than a gap, because nothing downstream can tell them apart.
 */
export function toNumber(raw: string): number | null {
  const v = String(raw ?? "").trim();
  if (!v || v === "-" || v === "—" || v.toLowerCase() === "n/a" || v.toLowerCase() === "null") {
    return null;
  }
  const negated = /^\(.*\)$/.test(v);
  const cleaned = v
    .replace(/^\(|\)$/g, "")
    .replace(/[,$\s]/g, "")
    .replace(/%$/, "");
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return negated ? -n : n;
}

export interface HeaderDiscovery {
  /** 0-based index of the row that looks like the header. */
  rowIndex: number;
  /** The header labels found on that row. */
  headers: string[];
  /** 0-1. Below ~0.5 the caller should ask the user rather than proceed. */
  confidence: number;
  /** Plain-language justification, so the UI can SHOW ITS WORK rather than assert. */
  reason: string;
  /** Rows above the header — a banner, a title, an export note. */
  skippedRows: number;
}

/**
 * Find the header row by scoring, instead of assuming row 1.
 *
 * Scored on the four properties a header actually has, none of which depend on the
 * file's domain:
 *
 *   · it is WIDE — close to the widest row in the sheet
 *   · its cells are DISTINCT — repeated labels are rare in a header
 *   · its cells are TEXT — a row of numbers is data, not a header
 *   · the rows BELOW it are populated and more numeric than it is
 *
 * The result carries a confidence and a reason on purpose. An adaptive parser that
 * silently picks a row is worse than one that asks: a wrong header maps every
 * column to the wrong field, and in a financial import that writes wrong numbers
 * rather than failing. `reason` is what lets the UI say "I think row 4 is your
 * header, and here are the 3 rows I skipped."
 */
export function discoverHeaderRow(grid: Grid, searchDepth = 30): HeaderDiscovery {
  const limit = Math.min(searchDepth, grid.length);
  const widest = Math.max(1, ...grid.slice(0, limit).map((r) => r.filter(Boolean).length));

  let best: HeaderDiscovery = {
    rowIndex: 0,
    headers: (grid[0] ?? []).map((c) => c.trim()),
    confidence: 0,
    reason: "No row scored as a header; defaulted to the first row.",
    skippedRows: 0,
  };

  for (let r = 0; r < limit; r++) {
    const row = (grid[r] ?? []).map((c) => (c ?? "").trim());
    const filled = row.filter(Boolean);
    if (filled.length < 2) continue;

    const width = filled.length / widest;
    const distinct = new Set(filled.map((c) => c.toLowerCase())).size / filled.length;
    const textish = filled.filter((c) => toNumber(c) === null).length / filled.length;

    // Look at the next few rows: a header has data under it, and that data is more
    // numeric than the header itself.
    const below = grid.slice(r + 1, r + 6);
    const belowFilled = below.length
      ? below.reduce((a, rw) => a + rw.filter(Boolean).length, 0) / (below.length * widest)
      : 0;
    const belowNumeric = below.length
      ? below.reduce((a, rw) => {
          const cells = rw.filter(Boolean);
          return (
            a + (cells.length ? cells.filter((c) => toNumber(c) !== null).length / cells.length : 0)
          );
        }, 0) / below.length
      : 0;

    const score =
      width * 0.3 + distinct * 0.2 + textish * 0.2 + belowFilled * 0.15 + belowNumeric * 0.15;

    if (score > best.confidence) {
      best = {
        rowIndex: r,
        headers: row,
        confidence: Number(score.toFixed(3)),
        reason:
          `Row ${r + 1} has ${filled.length} of ${widest} columns filled, ` +
          `${Math.round(distinct * 100)}% distinct labels, ` +
          `${Math.round(textish * 100)}% non-numeric, ` +
          `and the rows beneath it are ${Math.round(belowNumeric * 100)}% numeric.`,
        skippedRows: r,
      };
    }
  }
  return best;
}

/**
 * Find a row by what its first cell SAYS.
 *
 * The counterpart to `discoverHeaderRow` for files with a known landmark — a
 * GuruFocus export always has a `Fiscal Period` row, wherever it happens to sit.
 * Anchoring on content is what lets one parser read layouts whose row offsets,
 * widths and punctuation all differ.
 */
export function findRowByLabel(grid: Grid, pattern: RegExp, searchDepth = 60): number {
  const limit = Math.min(searchDepth, grid.length);
  for (let r = 0; r < limit; r++) {
    if (pattern.test((grid[r]?.[0] ?? "").trim())) return r;
  }
  return -1;
}

/** Find a cell anywhere in a row range, returning its column. Used for section markers. */
export function findColumnByLabel(
  grid: Grid,
  pattern: RegExp,
  fromRow: number,
  toRow: number,
): number | null {
  for (let r = Math.max(0, fromRow); r < Math.min(toRow, grid.length); r++) {
    const row = grid[r] ?? [];
    for (let c = 0; c < row.length; c++) {
      if (pattern.test((row[c] ?? "").trim())) return c;
    }
  }
  return null;
}
